import type { Vehicle } from '../../data/vehicles'
import { vehicleSpecificationSections } from '../../data/vehicle-template'
import { useInventoryTranslation } from './useInventoryTranslation'
import { useVehicleFactLabels } from './VehicleFacts'

export function safeVehicleUrl(value: string) {
  if (value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')) return value
  try { return new URL(value).protocol === 'https:' ? value : undefined } catch { return undefined }
}

export function VehicleSpecifications({ vehicle }: { vehicle: Vehicle }) {
  const { language, t } = useInventoryTranslation()
  const number = new Intl.NumberFormat(language === 'it' ? 'it-IT' : 'en-GB', { maximumFractionDigits: 2 })
  return (
    <div className="vehicle-specifications page-frame">
      {vehicleSpecificationSections.map((section) => {
        const fields = section.fields.flatMap((field) => {
          const value = vehicle.specifications[field.key]
          if (value === undefined || value === null || value === '') return []
          const formatted = typeof value === 'object' ? value[language]
            : typeof value === 'boolean' ? t(value ? 'valueYes' : 'valueNo')
              : typeof value === 'number' ? number.format(value) : value
          return formatted ? [{ ...field, formatted }] : []
        })
        if (!fields.length) return null
        return (
          <section key={section.id} className="vehicle-specification-section" aria-labelledby={`spec-${section.id}`}>
            <h2 id={`spec-${section.id}`}>{section.title[language]}</h2>
            <dl className="vehicle-facts">
              {fields.map((field) => <div key={field.key}><dt>{field.label[language]}</dt><dd>{field.formatted}{field.unit ? ` ${field.unit}` : ''}</dd></div>)}
            </dl>
          </section>
        )
      })}
      {vehicle.extraDetails?.length ? <section className="vehicle-specification-section" aria-labelledby="spec-extra">
        <h2 id="spec-extra">{t('extraDetailsTitle')}</h2>
        <dl className="vehicle-facts">{vehicle.extraDetails.map((item) => <div key={item.id}><dt>{item.label[language]}</dt><dd>{item.value[language]}</dd></div>)}</dl>
      </section> : null}
    </div>
  )
}

const equipmentCategories = ['comfort', 'safety', 'technology', 'exterior', 'other'] as const
const equipmentKeys = { comfort: 'equipmentComfort', safety: 'equipmentSafety', technology: 'equipmentTechnology', exterior: 'equipmentExterior', other: 'equipmentOther' } as const

export function VehicleEquipment({ vehicle }: { vehicle: Vehicle }) {
  const { language, t } = useInventoryTranslation()
  return <section className="vehicle-equipment page-frame" aria-labelledby="vehicle-equipment-title">
    <div className="vehicle-section-heading"><h2 id="vehicle-equipment-title">{t('accessoriesTitle')}</h2><p>{t('equipmentIntro')}</p></div>
    {vehicle.accessories.length ? <div className="vehicle-equipment__groups">{equipmentCategories.map((category) => {
      const items = vehicle.accessories.filter((item) => item.category === category)
      if (!items.length) return null
      return <details key={category} open className="vehicle-equipment__group">
        <summary>{t(equipmentKeys[category])}<span>{items.length}</span></summary>
        <ul>{items.map((item) => <li key={item.id}>{item.label[language]}{item.kind === 'optional' ? <small>{t('optionalEquipment')}</small> : null}</li>)}</ul>
      </details>
    })}</div> : <p>{t('accessoriesEmpty')}</p>}
  </section>
}

export function VehicleAdditionalContent({ vehicle }: { vehicle: Vehicle }) {
  const { language, t } = useInventoryTranslation()
  const labels = useVehicleFactLabels()
  const documents = vehicle.documents.filter((document) => safeVehicleUrl(document.url))
  const videoUrl = vehicle.videoUrl && safeVehicleUrl(vehicle.videoUrl)
  const finance = vehicle.finance
  return <>
    {vehicle.description ? <section className="vehicle-description page-frame" aria-labelledby="vehicle-description-title"><h2 id="vehicle-description-title">{t('descriptionTitle')}</h2><p>{vehicle.description[language]}</p></section> : null}
    {finance ? <section className="vehicle-description page-frame" aria-labelledby="vehicle-finance-title">
      <h2 id="vehicle-finance-title">{t('financeTitle')}</h2>
      <dl className="vehicle-facts">
        <div><dt>{t('factsMonthlyPayment')}</dt><dd>{labels.money(finance.monthlyEuro)}</dd></div>
        <div><dt>{t('financeDeposit')}</dt><dd>{labels.money(finance.depositEuro)}</dd></div>
        <div><dt>{t('financeDuration')}</dt><dd>{finance.durationMonths}</dd></div>
        {([
          ['financedEuro', 'financeAmount'], ['finalPaymentEuro', 'financeFinalPayment'],
          ['feesEuro', 'financeFees'], ['totalDueEuro', 'financeTotal'],
        ] as const).map(([key, label]) => finance[key] !== undefined ? <div key={key}><dt>{t(label)}</dt><dd>{labels.money(finance[key])}</dd></div> : null)}
        {(['tanPercent', 'taegPercent'] as const).map((key) => finance[key] !== undefined ? <div key={key}><dt>{t(key)}</dt><dd>{new Intl.NumberFormat(language, { style: 'percent', maximumFractionDigits: 2 }).format(finance[key] / 100)}</dd></div> : null)}
      </dl>
      {finance.terms ? <p>{finance.terms[language]}</p> : null}
    </section> : null}
    {documents.length || videoUrl ? <section className="vehicle-description page-frame" aria-labelledby="vehicle-documents-title">
      <h2 id="vehicle-documents-title">{t('documentsTitle')}</h2>
      <ul>{documents.map((document) => <li key={document.id}><a href={safeVehicleUrl(document.url)} target="_blank" rel="noopener noreferrer">{document.title[language]}</a></li>)}
      {videoUrl ? <li><a href={videoUrl} target="_blank" rel="noopener noreferrer">{t('videoAction')}</a></li> : null}</ul>
    </section> : null}
  </>
}
